/* eslint-disable react/jsx-pascal-case */
/* eslint-disable camelcase */
import React from 'react'

// Hide sorting arrow icons
import './materialTable.css'

import EmptyDataParagraph from 'components/common/EmptyDataParagraph'
import ExportOverlay from 'components/dataTable/ExportOverlay'
import { TableData, useAlert, useSchemaState } from 'contexts'
import {
  MRT_Row,
  MRT_TableContainer,
  MRT_TablePagination,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  useMaterialReactTable,
  type MRT_PaginationState,
  type MRT_TableInstance,
} from 'material-react-table'
import { Box } from '@mui/material'
import { Customizations, TableKey } from 'models'
import { CustomizeColumnsButton } from 'components/dataTable/CustomizeColumnsButton'
import { theme } from 'utils/theme'
import useGenerateColumns from 'utils/hooks/useGenerateColumns'
import useGetCurrentVisibility from 'utils/hooks/useGetCurrentVisibility'
import useConstrainedTableContainer from 'utils/hooks/useConstrainedTableContainer'
import useCsvExport from 'utils/hooks/useCsvExport'
import { DataTableDownLoadButton } from '../DataTableDownLoadButton'
import { DataTableUploadButton } from '../DataTableUploadButton'
import AutoMailButton from '../AutoMailButton'

type MaterialTableProps = {
  data: Array<TableData>
  title: string
  onClick: (row: MRT_Row<TableData>) => void
  paginationController?: {
    state: MRT_PaginationState
    onChange: (
      updater:
        | MRT_PaginationState
        | ((prev: MRT_PaginationState) => MRT_PaginationState),
    ) => void
    rowCount: number
    isFetching?: boolean
  }
  onFetchAllPages?: (
    pageSize?: number,
    onProgress?: (info: { loaded: number; total: number }) => void,
    signal?: AbortSignal,
  ) => Promise<Array<TableData>>
  stickyPagination?: boolean
  constrainTableContainer?: boolean
  emptyStateMessage?: string
}

const FALLBACK_TABLE_SCROLL_HEIGHT = 200
const TABLE_SCROLL_VERTICAL_SPACING = 32

const TableEmptyRows = ({ message }: { message: string }) => (
  <Box
    sx={{
      width: '100%',
      padding: '24px',
      textAlign: 'center',
    }}
  >
    <EmptyDataParagraph customSentence={message} />
  </Box>
)

const createEmptyRowsFallback = (
  message: string,
): ((props: { table: MRT_TableInstance<TableData> }) => React.ReactNode) => {
  return () => <TableEmptyRows message={message} />
}

const MaterialTable = ({
  data,
  onClick,
  title,
  paginationController,
  onFetchAllPages,
  stickyPagination,
  constrainTableContainer = false,
  emptyStateMessage,
}: MaterialTableProps) => {
  const noClickList: Array<keyof Customizations> = ['reserveTTCHF', 'observationsVendeur']

  const exportableTables: Array<TableKey> = [
    'futureSales',
    'salesResult',
    'history',
    'waitingFiles',
    'stock',
  ]

  const { displayAlert } = useAlert()
  const { currentTableKey, order } = useSchemaState()
  const { visibility } = useGetCurrentVisibility()
  const columns = useGenerateColumns()
  const { isExporting, exportProgress, handleDownload, cancelExport } = useCsvExport({
    title,
    data,
    onFetchAllPages,
    displayAlert,
  })

  const manualPagination = Boolean(paginationController)
  const paginationState = paginationController?.state
  const totalRowCount = paginationController?.rowCount
  const isTableFetching = paginationController?.isFetching ?? false
  const pageSize = paginationState?.pageSize
  const dataLength = data.length

  // ✅ (Fix 3) Guard: en mode manualPagination, la table doit recevoir uniquement les rows de la page courante.
  // On ne slice PAS ici (pour ne pas casser un backend qui renverrait volontairement > pageSize),
  // mais on log un warning en dev si ça ressemble à un dataset complet.
  React.useEffect(() => {
    if (!manualPagination || process.env.NODE_ENV === 'production' || typeof pageSize !== 'number') {
      return
    }

    if (dataLength > pageSize) {
      // eslint-disable-next-line no-console
      console.warn(
        `[MaterialTable] manualPagination=true mais data.length (${dataLength}) > pageSize (${pageSize}). ` +
        `Assure-toi de passer uniquement les rows de la page courante (server-side pagination).`,
      )
    }
  }, [dataLength, manualPagination, pageSize])

  const paginationRef = React.useRef<HTMLDivElement | null>(null)
  const shouldConstrainTable = constrainTableContainer
  const tableContainerId = `${currentTableKey}-table-container`
  const { tableContainerProps, tableScrollWrapperSx } = useConstrainedTableContainer({
    enabled: shouldConstrainTable,
    containerId: tableContainerId,
    paginationRef,
    verticalSpacing: TABLE_SCROLL_VERTICAL_SPACING,
    fallbackHeight: FALLBACK_TABLE_SCROLL_HEIGHT,
    paginationVisible: manualPagination,
  })

  const emptyRowsFallback = React.useMemo(() => {
    if (!emptyStateMessage) {
      return undefined
    }

    return createEmptyRowsFallback(emptyStateMessage)
  }, [emptyStateMessage])

  const table = useMaterialReactTable({
    data,
    layoutMode: 'semantic',
    columns,

    enableColumnOrdering: false,
    enableColumnResizing: true,
    enableHiding: false,
    enableStickyHeader: shouldConstrainTable,

    // ✅ Filtres colonnes + pagination
    enableColumnFilters: true,
    enableGlobalFilter: false,
    enablePagination: manualPagination,

    // On gère l’affichage de la pagination nous-mêmes, donc pas de bottom toolbar MRT
    enableBottomToolbar: false,
    muiTableContainerProps: tableContainerProps,
    renderEmptyRowsFallback: emptyRowsFallback,

    state: {
      columnVisibility: visibility,
      columnOrder: order,
      ...(manualPagination && paginationState ? { pagination: paginationState } : {}),
      isLoading: manualPagination ? isTableFetching : false,
    },
    initialState: {
      density: 'compact',
    },
    manualPagination,
    rowCount: manualPagination ? totalRowCount : undefined,
    onPaginationChange: manualPagination ? paginationController?.onChange : undefined,

    defaultColumn: {
      maxSize: 300,
      minSize: 1.5,
      size: 1,
    },
    muiTableProps: {
      size: 'small',
    },

    muiTableBodyCellProps: ({ row, column }) => ({
      onClick: () => {
        if (!noClickList.includes(column.id as keyof Customizations)) {
          onClick(row)
        }
      },
      sx: {
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      },
    }),
  })

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr)',
      }}
      key={`${currentTableKey}`}
      id={`${currentTableKey}-table-box`}
    >
      <Box
        // TITLE and TOOLBAR
        sx={{
          display: 'flex',
          backgroundColor: 'inherit',
          borderRadius: '4px',
          flexDirection: 'row',
          gap: '16px',
          justifyContent: 'space-between',
          padding: '0px',
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,

          // ✅ (Fix 1) Media query valide
          '@media (max-width: 768px)': {
            flexDirection: 'column',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',

            // ✅ (Fix 2) valeur CSS valide
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            padding: '4px 16px',
            borderRadius: '8px 8px 0px 0px',
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.secondary.contrastText,
            flexWrap: 'wrap',
            gap: '8px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '8px',
            }}
          >
            <p className="flex items-center">{title}</p>
            <CustomizeColumnsButton />
            {exportableTables.includes(currentTableKey) && <AutoMailButton />}
          </Box>

          <div className="flex flex-wrap gap-2 justify-end">
            <DataTableDownLoadButton
              onClick={handleDownload}
              disabled={isExporting}
              isLoading={isExporting}
            />

            {currentTableKey === 'futureSalesDetail' && <DataTableUploadButton />}

            <MRT_ToggleFiltersButton
              table={table}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.secondary.contrastText,
              }}
            />

            <MRT_ToggleDensePaddingButton
              table={table}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.secondary.contrastText,
              }}
            />
          </div>
        </Box>
      </Box>

      <Box sx={tableScrollWrapperSx}>
        <MRT_TableContainer
          id={tableContainerId}
          table={table}
          className="border border-primary"
        />
      </Box>

      {/* ✅ Pagination en bas */}
      {manualPagination && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '8px 8px',
            borderLeft: '1px solid',
            borderRight: '1px solid',
            borderBottom: '1px solid',
            borderColor: 'white',
            borderRadius: '0px 0px 8px 8px',
            backgroundColor: 'white',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            overflowX: 'hidden',
          }}
          ref={paginationRef}
        >
          <Box
            sx={{
              position: stickyPagination ? 'sticky' : 'static',
              bottom: stickyPagination ? 16 : 'auto',
              zIndex: stickyPagination ? 10 : 'auto',
              backgroundColor: stickyPagination ? 'white' : 'inherit',
              borderRadius: stickyPagination ? '8px' : '0px',
            }}
            className="pagination-wrapper"
          >
            <MRT_TablePagination table={table} />
          </Box>
        </Box>
      )}

      <ExportOverlay
        open={isExporting}
        progress={exportProgress}
        onCancel={cancelExport}
      />
    </Box>
  )
}

export default MaterialTable
